import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import { 
  ArrowLeft, Send, Bot, User, Sparkles, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const MASCOT_IMAGE = "https://customer-assets.emergentagent.com/job_a00bdaaa-d343-44f1-adb9-f8b11a8d38bf/artifacts/m964dse2_9C7E8ACF-CDB9-4A1C-A556-DF501523912B.jpeg";

export default function Assistant() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hey ${user?.first_name || "there"}! I'm Bitcoin Brain, your AI assistant for all things Bitcoin. Ask me anything about Bitcoin, and I'll explain it in plain English — no jargon, no hype, just the truth. What would you like to learn about?`
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [queriesRemaining, setQueriesRemaining] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await api.post("/ai/assistant", {
        query: userMessage
      });
      
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: res.data.response 
      }]);
      setQueriesRemaining(res.data.queries_remaining);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Daily query limit reached. Upgrade for more!");
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "You've reached your daily query limit. Upgrade your plan for more AI assistance, or come back tomorrow!",
          isError: true
        }]);
      } else {
        toast.error("Failed to get response");
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "Sorry, I'm having trouble right now. Please try again in a moment.",
          isError: true
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "What is Bitcoin in simple terms?",
    "Why is the 21 million cap important?",
    "How do I store Bitcoin safely?",
    "What is a seed phrase?",
    "Why do people say 'not your keys, not your coins'?"
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Dashboard</span>
            </button>
            <div className="flex items-center gap-3">
              <img src={MASCOT_IMAGE} alt="Bitcoin Brain" className="w-8 h-8 rounded-lg" />
              <span className="font-heading text-lg font-bold text-white">
                AI <span className="text-[#F7931A]">ASSISTANT</span>
              </span>
            </div>
            {queriesRemaining !== null && queriesRemaining !== "unlimited" && (
              <span className="text-zinc-500 text-sm">
                {queriesRemaining} left today
              </span>
            )}
            {queriesRemaining === "unlimited" && (
              <span className="text-[#F7931A] text-sm flex items-center gap-1">
                <Sparkles size={14} />
                Unlimited
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <AnimatePresence initial={false}>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-4 ${message.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${
                  message.role === "user" 
                    ? "bg-[#F7931A]/20" 
                    : message.isError 
                      ? "bg-red-500/20" 
                      : "bg-[#0A0A0A] border border-white/10"
                }`}>
                  {message.role === "user" ? (
                    <User className="text-[#F7931A]" size={20} />
                  ) : message.isError ? (
                    <AlertCircle className="text-red-400" size={20} />
                  ) : (
                    <img src={MASCOT_IMAGE} alt="AI" className="w-8 h-8 rounded-md" />
                  )}
                </div>
                <div className={`flex-1 ${message.role === "user" ? "text-right" : ""}`}>
                  <div className={`inline-block rounded-2xl px-4 py-3 max-w-[85%] ${
                    message.role === "user" 
                      ? "bg-[#F7931A] text-black" 
                      : message.isError
                        ? "bg-red-500/10 border border-red-500/30 text-red-300"
                        : "bg-[#0A0A0A] border border-white/10 text-zinc-300"
                  }`}>
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
                <img src={MASCOT_IMAGE} alt="AI" className="w-8 h-8 rounded-md" />
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-[#F7931A] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-[#F7931A] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-[#F7931A] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Suggested Questions */}
      {messages.length <= 1 && (
        <div className="px-4 pb-4">
          <div className="max-w-4xl mx-auto">
            <p className="text-zinc-500 text-sm mb-3">Suggested questions:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((question, i) => (
                <button
                  key={i}
                  onClick={() => setInput(question)}
                  className="bg-[#0A0A0A] border border-white/10 rounded-full px-4 py-2 text-sm text-zinc-400 hover:border-[#F7931A]/50 hover:text-white transition-all"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-white/5 p-4 glass-darker">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="flex gap-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about Bitcoin..."
              className="flex-1 bg-[#0A0A0A] border-white/10 text-white min-h-[48px] max-h-32 resize-none"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              data-testid="assistant-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="btn-primary h-12 w-12 p-0"
              data-testid="assistant-send-btn"
            >
              <Send size={20} />
            </Button>
          </div>
          <p className="text-zinc-600 text-xs mt-2 text-center">
            Bitcoin Brain gives educational info, not financial advice. Always do your own research.
          </p>
        </form>
      </div>
    </div>
  );
}
